import { noop } from 'lodash';
import { mixpanel } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import { MIXPANEL_FIELD_NAMES, MP_EVENTS, MIXPANEL_EVENT_VARIATION } from '../consts';
import { removeEmptyProps, flattenNotableClients } from '../utils';

const baseMixpanelEventHandler = ({ seller = {}, client = {}, allClients, origin } = {}) => {
    const { userId, userGuid } = getContext();
    const { USER_GUID, USER_ID, SELLER_ID, SELLER_NAME, CLIENT_NAME, CLIENTS_COUNT, CLIENT_NAMES, ORIGIN } = MIXPANEL_FIELD_NAMES;

    return removeEmptyProps({
        [USER_ID]: userId,
        [USER_GUID]: userGuid,
        [SELLER_NAME]: seller.name,
        [SELLER_ID]: seller.id,
        [CLIENT_NAME]: client.name,
        [CLIENTS_COUNT]: allClients?.length,
        [CLIENT_NAMES]: flattenNotableClients(allClients),
        [ORIGIN]: origin
    });
};

export const mixpanelEventHandlers = {
    [MP_EVENTS.SELLER_PAGE_CLIENTS_INTERACTION]: {
        name: 'Seller page - notable clients interaction',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.CHECKOUT_PAGE_CLIENTS_VIEWED]: {
        name: 'Checkout page - notable clients viewed',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.CHECKOUT_PAGE_CLIENTS_INTERACTION]: {
        name: 'Checkout page - notable clients intercation',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.LISTING_PAGE_CLIENTS_VIEWED]: {
        name: 'Listing page - notable clients viewed',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.LISTING_PAGE_CLIENTS_INTERACTION]: {
        name: 'Listing page - notable clients interaction',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.UNKNOWN_PAGE_CLIENTS_VIEW]: {
        name: 'Unknown - notable clients viewed',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.UNKNOWN_PAGE_CLIENTS_INTERACTION]: {
        name: 'Unknown - notable clients interaction',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.GIG_PAGE_VERIFIED_TOP_VIEWED]: {
        name: 'gig page - top verified badge - impression',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.GIG_PAGE_VERIFIED_TOP_INTERACTION]: {
        name: 'gig page - top verified badge - hover',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.GIG_PAGE_VERIFIED_BOTTOM_VIEWED]: {
        name: 'gig page - bottom verified badge - impression',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.GIG_PAGE_VERIFIED_BOTTOM_INTERACTION]: {
        name: 'gig page - bottom verified badge - hover',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.SELLER_PAGE_VERIFIED_TOP_VIEWED]: {
        name: 'seller page - top verified badge - impression',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.SELLER_PAGE_VERIFIED_TOP_INTERACTION]: {
        name: 'seller page - top verified badge - hover',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.SELLER_PAGE_VERIFIED_BOTTOM_VIEWED]: {
        name: 'seller page - verified section - impression',
        handler: baseMixpanelEventHandler
    },
    [MP_EVENTS.SELLER_PAGE_VERIFIED_BOTTOM_INTERACTION]: {
        name: 'seller page - verified section - hover',
        handler: baseMixpanelEventHandler
    }
};

const generateTrackerFunc = (type) => (params = {}) => {
    const { name, handler } = mixpanelEventHandlers[type];

    mixpanel.track(
        name,
        handler(params)
    );
};

export const trackSellerPageInteraction = generateTrackerFunc(MP_EVENTS.SELLER_PAGE_CLIENTS_INTERACTION);

export const trackCheckoutPageView = generateTrackerFunc(MP_EVENTS.CHECKOUT_PAGE_CLIENTS_VIEWED);

export const trackCheckoutPageInteraction = generateTrackerFunc(MP_EVENTS.CHECKOUT_PAGE_CLIENTS_INTERACTION);

export const trackListingPageView = generateTrackerFunc(MP_EVENTS.LISTING_PAGE_CLIENTS_VIEWED);

export const trackListingPageInteraction = generateTrackerFunc(MP_EVENTS.LISTING_PAGE_CLIENTS_INTERACTION);

export const trackUnknownView = generateTrackerFunc(MP_EVENTS.UNKNOWN_PAGE_CLIENTS_VIEW);

export const trackUnknownInteraction = generateTrackerFunc(MP_EVENTS.UNKNOWN_PAGE_CLIENTS_INTERACTION);

export const trackGigTopPageView = generateTrackerFunc(MP_EVENTS.GIG_PAGE_VERIFIED_TOP_VIEWED);

export const trackGigTopPageInteraction = generateTrackerFunc(MP_EVENTS.GIG_PAGE_VERIFIED_TOP_INTERACTION);

export const trackGigBottomPageView = generateTrackerFunc(MP_EVENTS.GIG_PAGE_VERIFIED_BOTTOM_VIEWED);

export const trackGigBottomPageInteraction = generateTrackerFunc(MP_EVENTS.GIG_PAGE_VERIFIED_BOTTOM_INTERACTION);

export const trackSellerTopPageView = generateTrackerFunc(MP_EVENTS.SELLER_PAGE_VERIFIED_TOP_VIEWED);

export const trackSellerTopPageInteraction = generateTrackerFunc(MP_EVENTS.SELLER_PAGE_VERIFIED_TOP_INTERACTION);

export const trackSellerBottomPageView = generateTrackerFunc(MP_EVENTS.SELLER_PAGE_VERIFIED_BOTTOM_VIEWED);

export const trackSellerBottomPageInteraction = generateTrackerFunc(MP_EVENTS.SELLER_PAGE_VERIFIED_BOTTOM_INTERACTION);

const pageTrackerMapper = {
    [MP_EVENTS.SELLER_PAGE_CLIENTS_INTERACTION]: trackSellerPageInteraction,
    [MP_EVENTS.CHECKOUT_PAGE_CLIENTS_VIEWED]: trackCheckoutPageView,
    [MP_EVENTS.CHECKOUT_PAGE_CLIENTS_INTERACTION]: trackCheckoutPageInteraction,
    [MP_EVENTS.LISTING_PAGE_CLIENTS_VIEWED]: trackListingPageView,
    [MP_EVENTS.LISTING_PAGE_CLIENTS_INTERACTION]: trackListingPageInteraction,
    [MP_EVENTS.GIG_PAGE_VERIFIED_TOP_VIEWED]: trackGigTopPageView,
    [MP_EVENTS.GIG_PAGE_VERIFIED_TOP_INTERACTION]: trackGigTopPageInteraction,
    [MP_EVENTS.GIG_PAGE_VERIFIED_BOTTOM_VIEWED]: trackGigBottomPageView,
    [MP_EVENTS.GIG_PAGE_VERIFIED_BOTTOM_INTERACTION]: trackGigBottomPageInteraction,
    [MP_EVENTS.SELLER_PAGE_VERIFIED_TOP_VIEWED]: trackSellerTopPageView,
    [MP_EVENTS.SELLER_PAGE_VERIFIED_TOP_INTERACTION]: trackSellerTopPageInteraction,
    [MP_EVENTS.SELLER_PAGE_VERIFIED_BOTTOM_VIEWED]: trackSellerBottomPageView,
    [MP_EVENTS.SELLER_PAGE_VERIFIED_BOTTOM_INTERACTION]: trackSellerBottomPageInteraction
};

const generateTrackByOrigin = (variation) => (params = {}, defaultTracker = noop) => {
    const { origin } = params;
    const { DELIMITER } = MIXPANEL_EVENT_VARIATION;
    let tracker = defaultTracker;

    if (origin) {
        const mixpanelEventType = Object.values(MP_EVENTS).find((currentType) => currentType.includes(origin) && currentType.includes(`${DELIMITER}${variation}`));

        if (mixpanelEventType) {
            const locatedtracker = pageTrackerMapper[mixpanelEventType];

            if (locatedtracker) {
                tracker = locatedtracker;
            }
        }
    }

    tracker(params);
};

export const trackViewByOrigin = generateTrackByOrigin(MIXPANEL_EVENT_VARIATION.VIEW);

export const trackInteractionByOrigin = generateTrackByOrigin(MIXPANEL_EVENT_VARIATION.INTERACTION);
